import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { useChallenge } from "@/contexts/ChallengeContext";
import { useRefreshOnResume } from "@/hooks/useRefreshOnResume";
import { track } from "@/integrations/posthog/client";
import { openExternalUrl } from "@/utils/linkify";
import {
  fetchActivePopups,
  hasCommunityEnrollment,
  markPopupSeen,
  markPopupCtaClicked,
  type PopupCampaign,
} from "@/services/popupService";
import { pickPopup, type UserPopupState, type ActiveEnrollmentState } from "@/lib/popupTriggers";
import { nativeCampaignCta } from "@/lib/externalLinkPolicy";
import { fetchIapEnabled } from "@/hooks/useIapEnabled";
import { IN_APP_PAYWALL_PATH } from "@/lib/inAppSubscribe";
import EntryPopupModal from "@/components/popups/EntryPopupModal";

/**
 * Root-level host that fetches active popup campaigns, matches the user's current
 * challenge state against each campaign's trigger, shows at most one popup per
 * entrance, records the "seen" row, and routes the CTA. Renders null until a
 * popup matches. A failed fetch is non-fatal and never blocks the app.
 */
const EntryPopupHost = () => {
  const navigate = useNavigate();
  // ChallengeContext exposes the hook as `useChallenge` and tracks a SINGLE active
  // challenge (not an array). The current day is already computed + clamped on
  // `progress.currentDay`; total days is surfaced via `currentChallengeTotalDays`.
  const { currentChallengeType, currentChallengeTotalDays, progress } = useChallenge();
  const [active, setActive] = useState<PopupCampaign | null>(null);
  const shownThisSession = useRef<Set<string>>(new Set());
  // In-flight guard: challenge state loads async, so `evaluate` re-runs as it
  // settles. Without this, overlapping runs could launch concurrent fetches and
  // race past the post-await `shownThisSession` check.
  const evaluatingRef = useRef(false);
  const iapEnabledRef = useRef(false);

  const buildState = useCallback((): UserPopupState => {
    const enrollments: ActiveEnrollmentState[] = [];
    // Only push an enrollment when there's a live active challenge. With no active
    // challenge the array is empty, so only the "everyone" trigger can match.
    if (currentChallengeType && progress) {
      enrollments.push({
        challengeType: currentChallengeType, // 'entry' | 'community' | 'course'
        currentDay: progress.currentDay,
        totalDays: currentChallengeTotalDays ?? 0,
      });
    }
    return { enrollments };
  }, [currentChallengeType, currentChallengeTotalDays, progress]);

  const evaluate = useCallback(async () => {
    if (active) return; // one popup per entrance
    if (evaluatingRef.current) return; // only one evaluate in flight at a time
    evaluatingRef.current = true;
    try {
      // Drop checkout-bound campaigns on native BEFORE picking, so a blocked
      // popup doesn't consume the one-per-entrance slot and silently suppress a
      // legitimate lower-priority one. Admins can point a CTA anywhere, and a
      // native tap that reaches our web checkout is an App Store 3.1.1 rejection
      // — see @/lib/externalLinkPolicy. Web keeps every campaign.
      // Since IAP: when store purchases are live, a checkout-bound popup still
      // shows and its tap goes to the in-app paywall instead (see handleCta).
      const isNative = Capacitor.isNativePlatform();
      const [fetched, iapEnabled] = await Promise.all([fetchActivePopups(), fetchIapEnabled()]);
      iapEnabledRef.current = iapEnabled;
      const campaigns = fetched.filter(
        (c) => nativeCampaignCta(c, isNative, iapEnabled) !== "hidden",
      );
      const state = buildState();
      // `community_all` means ANY community enrollment, not "community active".
      // Only pay for the extra query when a candidate needs it and the active
      // challenge isn't already community. A synthetic enrollment lets the
      // unchanged `matchesTrigger` (enrollments.some(community)) resolve true.
      const needsCommunityCheck =
        campaigns.some((c) => c.trigger_type === "community_all") &&
        !state.enrollments.some((e) => e.challengeType === "community");
      if (needsCommunityCheck && (await hasCommunityEnrollment())) {
        state.enrollments.push({ challengeType: "community", currentDay: 0, totalDays: 0 });
      }
      const picked = pickPopup(campaigns, state);
      if (!picked || shownThisSession.current.has(picked.id)) return;
      shownThisSession.current.add(picked.id);
      setActive(picked);
      track("popup_shown", { campaign_id: picked.id, trigger_type: picked.trigger_type });
      void markPopupSeen(picked.id);
    } catch {
      // non-fatal: a failed popup fetch must never block the app
    } finally {
      evaluatingRef.current = false;
    }
  }, [active, buildState]);

  // Re-run whenever `evaluate` changes — i.e. when challenge readiness
  // (currentChallengeType / progress / totalDays via buildState) settles after
  // mount. The `active` gate + `shownThisSession` ref keep it to one show/seen
  // per entrance; `evaluatingRef` serializes the async runs.
  useEffect(() => {
    void evaluate();
  }, [evaluate]);

  useRefreshOnResume(() => {
    void evaluate();
  });

  const handleClose = () => {
    if (active) track("popup_dismissed", { campaign_id: active.id, trigger_type: active.trigger_type });
    setActive(null);
  };

  const handleCta = () => {
    if (!active) return;
    track("popup_cta_clicked", { campaign_id: active.id, trigger_type: active.trigger_type });
    void markPopupCtaClicked(active.id);
    const { cta_action_type, cta_value } = active;
    const ctaMode = nativeCampaignCta(active, Capacitor.isNativePlatform(), iapEnabledRef.current);
    setActive(null);
    if (!cta_value) return;
    if (ctaMode === "paywall") {
      navigate(IN_APP_PAYWALL_PATH);
    } else if (cta_action_type === "external_url") {
      // Native-safe: opens in the Capacitor in-app browser on device, new tab on web.
      // Also normalizes a missing scheme (example.com -> https://example.com).
      void openExternalUrl(cta_value);
    } else {
      navigate(cta_value);
    }
  };

  if (!active) return null;
  return <EntryPopupModal campaign={active} open onClose={handleClose} onCtaClick={handleCta} />;
};

export default EntryPopupHost;
